// React hook for auth state. Wraps Supabase's onAuthStateChange so any
// component can call useAuth() and re-render when sign-in / sign-out happens.
import { useEffect, useState, useCallback } from 'react';
import { supabase, supabaseConfigured } from './supabase.js';

export function useAuth() {
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Load initial session + listen for changes.
  useEffect(() => {
    if (!supabaseConfigured) {
      setLoading(false);
      return;
    }
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setUser(data.session?.user || null);
    }).finally(() => mounted && setLoading(false));

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  // Whenever the user changes, refetch their profile.
  useEffect(() => {
    if (!user || !supabaseConfigured) {
      setProfile(null);
      return;
    }
    let alive = true;
    supabase
      .from('profiles')
      .select('user_id, slug, display_name, member_number, is_admin')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (!alive) return;
        if (err) setError(err.message);
        setProfile(data || null);
      });
    return () => { alive = false; };
  }, [user]);

  const signIn = useCallback(async (email, password) => {
    if (!supabaseConfigured) {
      throw new Error('Supabase is not configured. Set VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY.');
    }
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) throw err;
    return data.user;
  }, []);

  const signOut = useCallback(async () => {
    if (!supabaseConfigured) return;
    await supabase.auth.signOut();
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user || !supabaseConfigured) return;
    const { data } = await supabase
      .from('profiles')
      .select('user_id, slug, display_name, member_number, is_admin')
      .eq('user_id', user.id)
      .maybeSingle();
    setProfile(data || null);
  }, [user]);

  return { user, profile, loading, error, signIn, signOut, refreshProfile };
}

// Lookup another person's profile by slug (used by /u/:slug pages).
export async function getProfileBySlug(slug) {
  if (!supabaseConfigured) return null;
  const { data } = await supabase
    .from('profiles')
    .select('user_id, slug, display_name, member_number, is_admin, created_at')
    .eq('slug', slug)
    .maybeSingle();
  return data;
}
